import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { TransformedOrder } from "../types/orderApiTypes";
import Icon from "../../../../common/components/Icon/Icon";

interface OrderBacsDetailsProps {
  order: TransformedOrder;
}

const OrderBacsDetails: React.FC<OrderBacsDetailsProps> = ({ order }) => {
  const { t } = useTranslation();
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const { paymentMethodInfo } = order;

  // Render nothing if it's not a BACS payment or there are no account details
  if (
    paymentMethodInfo.methodId !== "bacs" ||
    !paymentMethodInfo.accountDetails ||
    paymentMethodInfo.accountDetails.length === 0
  ) {
    return null;
  }

  const handleCopyToClipboard = (
    fieldName: string,
    textToCopy: string | undefined
  ) => {
    if (!textToCopy) return;

    navigator.clipboard.writeText(textToCopy).then(
      () => {
        setCopiedField(fieldName);
        setTimeout(() => setCopiedField(null), 2000); // Reset after 2 seconds
        toast.success(t("orderPage.bacsDetails.copySuccess", { fieldName }));
      },
      (err) => {
        toast.error(t("orderPage.bacsDetails.copyError", { fieldName }));
        console.error("Failed to copy text: ", err);
      }
    );
  };

  return (
    <div className="order-page__bacs-details">
      <h3 className="order-page__bacs-details__title">
        {t("orderPage.bacsDetails.title", "Our Bank Accounts")}
      </h3>
      <div className="order-page__bacs-details__accounts-list">
        {paymentMethodInfo.accountDetails.map((account, index) => (
          <div key={index} className="order-page__bacs-details__account-card">
            <h4 className="order-page__bacs-details__bank-name">
              {account.bank_name}
            </h4>
            <dl className="order-page__bacs-details__details-list">
              <div className="order-page__bacs-details__detail-item">
                <dt>
                  {t("orderPage.bacsDetails.accountName", "Account Name")}
                </dt>
                <dd>{account.account_name}</dd>
              </div>
              <div className="order-page__bacs-details__detail-item">
                <dt>{t("orderPage.bacsDetails.iban", "IBAN")}</dt>
                <dd>
                  <span>{account.iban}</span>
                  <button
                    className="order-page__bacs-details__copy-btn"
                    onClick={() =>
                      handleCopyToClipboard(
                        t("orderPage.bacsDetails.iban", "IBAN"),
                        account.iban
                      )
                    }
                    aria-label={`Copy IBAN`}
                  >
                    {copiedField === t("orderPage.bacsDetails.iban", "IBAN") ? (
                      t("orderPage.bacsDetails.copied", "Copied!")
                    ) : (
                      <>
                        <Icon iconName="copy" />
                        {t("orderPage.bacsDetails.copy", "Copy")}
                      </>
                    )}
                  </button>
                </dd>
              </div>
              <div className="order-page__bacs-details__detail-item">
                <dt>
                  {t("orderPage.bacsDetails.accountNumber", "Account Number")}
                </dt>
                <dd>
                  <span>{account.account_number}</span>
                  <button
                    className="order-page__bacs-details__copy-btn"
                    onClick={() =>
                      handleCopyToClipboard(
                        t(
                          "orderPage.bacsDetails.accountNumber",
                          "Account Number"
                        ),
                        account.account_number
                      )
                    }
                    aria-label={`Copy Account Number`}
                  >
                    {copiedField ===
                    t(
                      "orderPage.bacsDetails.accountNumber",
                      "Account Number"
                    ) ? (
                      t("orderPage.bacsDetails.copied", "Copied!")
                    ) : (
                      <>
                        <Icon iconName="copy" />
                        {t("orderPage.bacsDetails.copy", "Copy")}
                      </>
                    )}
                  </button>
                </dd>
              </div>
            </dl>
          </div>
        ))}
      </div>
    </div>
  );
};

export default OrderBacsDetails;
